// Geo Model editor. Single -> one working location with N activation markets.
// Hub -> N pairs of working location -> activation markets.

import { Plus, X } from 'lucide-react';
import type { GeoModel, GeoModelPair } from '@rc/engine/types';
import CountryPicker, { COUNTRIES } from './CountryPicker.js';

interface Props {
  value: GeoModel;
  onChange: (next: GeoModel) => void;
}

export default function GeoPairsEditor({ value, onChange }: Props) {
  const update = (i: number, patch: Partial<GeoModelPair>) => {
    const next = { ...value, pairs: value.pairs.map((p, j) => (i === j ? { ...p, ...patch } : p)) };
    onChange(next);
  };
  const remove = (i: number) => {
    if (value.pairs.length <= 1) return;
    onChange({ ...value, pairs: value.pairs.filter((_, j) => j !== i) });
  };
  const add = () => {
    onChange({
      ...value,
      pairs: [...value.pairs, { workingLocation: 'US', activationMarkets: [] }],
    });
  };

  const setType = (type: 'single' | 'hub') => {
    if (type === 'single') {
      onChange({ type, pairs: value.pairs.slice(0, 1) });
    } else {
      onChange({ ...value, type });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="label !mb-0">Geo model</label>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {(['single', 'hub'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={
                value.type === t
                  ? 'px-3 py-1 text-xs font-medium bg-brand-purple-50 text-brand-purple-700'
                  : 'px-3 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50'
              }
            >
              {t === 'single' ? 'Single market' : 'Hub & spokes'}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {value.pairs.map((p, i) => (
          <div
            key={i}
            className="rounded-lg border border-slate-200 p-3 grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-3 items-start"
          >
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Working location</label>
              <select
                className="input"
                value={p.workingLocation}
                onChange={(e) => update(i, { workingLocation: e.target.value })}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Activation markets</label>
              <CountryPicker
                value={p.activationMarkets}
                onChange={(next) => update(i, { activationMarkets: next })}
                placeholder="Select activation markets…"
              />
            </div>
            {value.type === 'hub' && value.pairs.length > 1 && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-slate-400 hover:text-rose-500 mt-6"
                aria-label="Remove pair"
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
        {value.type === 'hub' && (
          <button
            type="button"
            onClick={add}
            className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add working location
          </button>
        )}
      </div>
    </div>
  );
}
